import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const METAL_BG =
  "linear-gradient(135deg,#FFFFFF 0%,#E4E6EA 22%,#A6ABB4 55%,#F1F2F4 78%,#898F99 100%)";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { checkSession } = useAuth();

  const [invite, setInvite] = useState(null); // null=loading, false=invalid, obj=valid
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setInvite(false); setError("Missing invite token."); return; }
    (async () => {
      try {
        const { data } = await api.get(`/auth/invite/${token}`);
        setInvite(data);
      } catch (e) {
        setInvite(false);
        setError(formatApiErrorDetail(e.response?.data?.detail) || "This invite link is no longer valid.");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setSubmitting(true);
    try {
      await api.post(`/auth/invite/${token}`, { password });
      await checkSession();
      setDone(true);
      setTimeout(() => navigate("/member"), 900);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="set-password-page"
      className="min-h-screen text-neutral-200 flex flex-col"
      style={{
        background: "radial-gradient(ellipse at 50% 0%, #2a2a2a 0%, #141414 40%, #050505 80%, #000000 100%)",
        fontFamily: "'Archivo', system-ui, sans-serif",
      }}
    >
      <header className="px-6 py-5">
        <Link to="/" aria-label="Wellnet home">
          <div
            aria-hidden
            className="w-40 h-5"
            style={{
              background: METAL_BG,
              WebkitMaskImage: "url(/wellnet-logo.png)", maskImage: "url(/wellnet-logo.png)",
              WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
              WebkitMaskSize: "contain", maskSize: "contain",
              WebkitMaskPosition: "left center", maskPosition: "left center",
            }}
          />
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-4">
        <div
          className="w-full max-w-md p-9 rounded-xl"
          style={{
            background: "radial-gradient(ellipse at 50% -10%, #202226 0%, #0e0f12 55%, #050506 100%)",
            border: "1px solid #22252A",
            boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
          }}
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">Welcome to Wellnet</div>
          <h1 className="text-2xl uppercase tracking-[0.3em] mb-6 text-neutral-100" style={{ fontWeight: 400 }}>
            {done ? "You're in" : "Set Password"}
          </h1>

          {invite === null && (
            <p className="text-neutral-400 text-sm">Checking your invite…</p>
          )}

          {invite === false && (
            <>
              <p className="text-neutral-400 text-sm mb-6">{error}</p>
              <Link
                to="/"
                data-testid="back-home-btn"
                className="w-full block text-center uppercase tracking-[0.3em] text-xs font-bold py-4 rounded-md"
                style={{ background: METAL_BG, color: "#08090B" }}
              >
                Back to Wellnet
              </Link>
            </>
          )}

          {invite && !done && (
            <>
              <p className="text-neutral-500 text-sm mb-8">
                Your membership was approved. Choose a password for{" "}
                <span className="text-neutral-200">{invite.email}</span>.
              </p>
              <form onSubmit={submit} className="space-y-6">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] mb-2 text-neutral-500">New Password</label>
                  <input
                    data-testid="new-password-input"
                    type="password" required minLength={6}
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="auth-line-input" autoComplete="new-password"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] mb-2 text-neutral-500">Confirm Password</label>
                  <input
                    data-testid="confirm-password-input"
                    type="password" required minLength={6}
                    value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    className="auth-line-input" autoComplete="new-password"
                  />
                </div>
                {error && (
                  <div data-testid="set-password-error" className="text-xs uppercase tracking-[0.2em] px-3 py-2" style={{ color: "#D9A05B", border: "1px solid #D9A05B" }}>
                    {error}
                  </div>
                )}
                <button
                  data-testid="set-password-submit"
                  type="submit" disabled={submitting}
                  className="w-full uppercase tracking-[0.3em] text-xs font-bold py-4 rounded-md disabled:opacity-60 transition-transform hover:-translate-y-0.5"
                  style={{ background: METAL_BG, color: "#08090B" }}
                >
                  {submitting ? "Please wait…" : "Create Account"}
                </button>
              </form>
            </>
          )}

          {done && (
            <p data-testid="set-password-success" className="text-neutral-400 text-sm">
              Account created. Taking you to your member portal…
            </p>
          )}
        </div>
      </main>

      <style>{`
        .auth-line-input {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid #31353C;
          color: #F3F4F6;
          padding: 8px 0;
          font-size: 18px;
          color-scheme: dark;
          transition: border-color .15s;
        }
        .auth-line-input:focus { outline: none; border-bottom-color: #C7CAD1; }
        .auth-line-input::placeholder { color: #6F737B; }
      `}</style>
    </div>
  );
}
